// Main application entry point
import { FileManager, VersionComparator } from './fileManager.js';
import { SettingsManager } from './settingsManager.js';
import { MilkdownEditorManager } from './milkdown.js';
import AuthManager from './auth.js';
import { PDFGenerator } from './generatePDF.js';

class DocumentAccelerationApp {
    constructor() {
        this.currentView = 'home';
        this.currentProject = null;
        this.config = null;
        this.excludedFromContext = new Set(); // Track files excluded from context
        this.pdfGenerator = new PDFGenerator();
        this.init();
    }

    // Helper methods for authenticated API calls
    async apiGet(url) {
        return AuthManager.get(url);
    }

    async apiPost(url, data) {
        return AuthManager.post(url, data);
    }

    async apiPut(url, data) {
        return AuthManager.put(url, data);
    }

    async apiDelete(url) {
        return AuthManager.delete(url);
    }

    async apiFetch(url, options) {
        return AuthManager.fetch(url, options);
    }

    async init() {
        // Check authentication
        if (!AuthManager.isAuthenticated()) {
            window.location.href = '/login.html';
            return;
        }

        await this.loadConfig();
        this.setupRouting();
        this.loadView();
        this.setupLogout();
    }

    setupLogout() {
        // Add logout button to header if not already present
        const logoutBtn = document.getElementById('logout-btn');
        if (logoutBtn) {
            logoutBtn.addEventListener('click', () => {
                AuthManager.logout();
            });
        }
    }

    async loadConfig() {
        try {
            const [personas, documentTypes, settings] = await Promise.all([
                AuthManager.get('/api/config/personas').then(r => r.json()),
                AuthManager.get('/api/document-types').then(r => r.json()),
                AuthManager.get('/api/config/settings').then(r => r.json())
            ]);

            this.config = { personas, documentTypes, settings };
        } catch (error) {
            console.error('Failed to load configuration:', error);
            this.config = { personas: {}, documentTypes: {}, settings: {} };
        }
    }

    setupRouting() {
        // Simple hash-based routing
        window.addEventListener('hashchange', () => {
            this.loadView();
        });
    }

    loadView() {
        const hash = window.location.hash.slice(1);
        const [view, ...params] = hash.split('/');

        switch (view) {
            case 'project':
                if (params[0]) {
                    this.currentProject = params[0];
                    this.currentView = 'project';
                }
                break;
            default:
                this.currentView = 'home';
        }

        this.render();
    }

    render() {
        const app = document.getElementById('app');

        if (this.currentView === 'home') {
            this.renderHomePage(app);
        } else if (this.currentView === 'project') {
            this.renderProjectPage(app);
        }
    }

    async renderHomePage(container) {
        container.innerHTML = `
            <div class="home-page-fullwidth">
                <header class="page-header">
                    <div class="header-content">
                        <div class="header-main">
                            <div class="header-branding">
                                <h1>✈️ Jet Stream: Strategic Document Acceleration</h1>
                                <p class="header-tagline">Highly curated, consistent and informed document assistant</p>
                            </div>
                        </div>
                        <div class="header-actions">
                            <button id="settings-btn" class="btn btn-secondary">⚙️ Settings</button>
                            <button id="logout-btn" class="btn btn-secondary">🚪 Logout</button>
                            <div class="header-stats">
                                <div class="stat-item">
                                    <span class="stat-number" id="project-count">0</span>
                                    <span class="stat-label">Projects</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </header>

                <div class="home-intro-section" id="home-intro-section">
                    <div class="intro-content">
                        <button class="intro-close-btn" id="close-intro-btn" title="Close">&times;</button>
                        <div class="intro-header">
                            <h3>Build AI Literacy Through Structured Collaboration</h3>
                            <p>Jet Stream helps you create professional documents while learning to work effectively with AI. Instead of generic outputs, our Plan/Act workflow teaches you to guide AI as a strategic partner, developing skills for agentic collaboration.</p>
                        </div>
                        <div class="intro-columns">
                            <div class="intro-column">
                                <div class="column-icon">🤔</div>
                                <h4>The Challenge</h4>
                                <p>Traditional AI document generation often produces <strong>incomplete or generic content</strong> because it lacks proper context and understanding of your specific needs.</p>
                                <ul>
                                    <li>Missing critical details</li>
                                    <li>Generic, one-size-fits-all content</li>
                                    <li>No structured information gathering</li>
                                    <li>Poor quality outputs</li>
                                </ul>
                            </div>
                            <div class="intro-column highlight">
                                <div class="column-icon">✨</div>
                                <h4>The Solution: Plan/Act</h4>
                                <div class="workflow-steps">
                                    <div class="workflow-phase">
                                        <strong>📋 PLAN Mode</strong>
                                        <p>AI asks strategic questions to gather comprehensive context, clarify requirements, and ensure nothing is missed.</p>
                                    </div>
                                    <div class="phase-arrow">↓</div>
                                    <div class="workflow-phase">
                                        <strong>✍️ ACT Mode</strong>
                                        <p>AI generates professional, complete documents using all gathered information and best practices.</p>
                                    </div>
                                </div>
                            </div>
                            <div class="intro-column">
                                <div class="column-icon">🎓</div>
                                <h4>Building AI Literacy</h4>
                                <p>This workflow teaches you to work <strong>with AI as a partner</strong>, not just a tool. By engaging in the planning conversation, you:</p>
                                <ul>
                                    <li>Learn what information makes documents effective</li>
                                    <li>Develop better prompting skills</li>
                                    <li>Understand how to guide AI iteratively</li>
                                    <li>Create a foundation for agentic collaboration</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="create-project-section">
                    <div class="create-project-grid">
                        <div class="project-info-section">
                            <h2>${this.getUserGreeting()}</h2>
                            <p>Start by giving your project a name and description, then choose your persona and document type.</p>
                            
                            <div class="form-group">
                                <label for="project-name">Project Name *</label>
                                <input type="text" id="project-name" placeholder="Enter project name..." required>
                            </div>
                            <div class="form-group">
                                <label for="project-description">Description</label>
                                <textarea id="project-description" rows="3" placeholder="Describe your project..."></textarea>
                            </div>
                            
                            <button id="create-project-btn" class="btn btn-primary btn-large" disabled>Create Project</button>
                        </div>

                        <div class="persona-selection-section">
                            <h3>Choose Persona</h3>
                            <div class="selection-cards" id="persona-cards">
                                ${this.renderPersonaCards()}
                            </div>
                        </div>

                        <div class="document-type-selection-section">
                            <h3>Choose Document Type</h3>
                            <div class="selection-cards" id="document-type-cards">
                                ${this.renderDocumentTypeCards(this.getDefaultPersona())}
                            </div>
                        </div>
                    </div>
                </div>

                <div class="projects-section">
                    <div class="section-header">
                        <h2>Your Projects</h2>
                    </div>
                    <div id="projects-list" class="projects-list">
                        <div class="loading">Loading projects...</div>
                    </div>
                </div>

                <div class="info-notice bottom">
                    <span class="info-icon">ℹ️</span>
                    <span class="info-text">
                        Want to contribute new document types or personas? 
                        <a href="https://source.tui/e2e-architecture/enterprise-architects/demos/jet-stream-document-accelerator" target="_blank" rel="noopener noreferrer">
                            Create a merge request on source.tui
                        </a>
                    </span>
                </div>

            </div>
        `;

        // Load and display projects
        this.loadProjects();

        // Setup event listeners
        this.setupHomePageEvents();
    }

    renderPersonasInfo() {
        if (!this.config.personas) return '<p>Loading personas...</p>';

        return Object.entries(this.config.personas).map(([key, persona]) => `
            <div class="persona-item">
                <strong>${persona.name}</strong>
                <p>${persona.description}</p>
            </div>
        `).join('');
    }

    renderDocumentTypesInfo() {
        if (!this.config.documentTypes) return '<p>Loading document types...</p>';

        return Object.entries(this.config.documentTypes).map(([key, docType]) => `
            <div class="document-type-item">
                <strong>${docType.name}</strong>
                <p>${docType.description}</p>
            </div>
        `).join('');
    }

    renderPersonaOptions() {
        if (!this.config.personas) return '<option>Loading...</option>';

        return Object.entries(this.config.personas).map(([key, persona]) =>
            `<option value="${key}">${persona.name}</option>`
        ).join('');
    }

    renderDocumentTypeOptions() {
        if (!this.config.documentTypes) return '<option>Loading...</option>';

        return Object.entries(this.config.documentTypes).map(([key, docType]) =>
            `<option value="${key}">${docType.name}</option>`
        ).join('');
    }

    renderPersonaCards() {
        if (!this.config.personas) return '<div class="loading-card">Loading personas...</div>';

        const defaultPersona = this.getDefaultPersona();

        return Object.entries(this.config.personas).map(([key, persona]) =>
            `<div class="selection-card ${key === defaultPersona ? 'selected' : ''}" data-value="${key}">
                <div class="card-header">
                    <h4>${persona.name}</h4>
                </div>
                <div class="card-description">
                    ${persona.description || 'Professional persona for document creation'}
                </div>
            </div>`
        ).join('');
    }

    renderDocumentTypeCards(selectedPersona = null) {
        if (!this.config.documentTypes) return '<div class="loading-card">Loading document types...</div>';

        // Filter document types by persona if one is selected
        let filteredDocTypes = this.config.documentTypes;

        if (selectedPersona) {
            filteredDocTypes = filteredDocTypes.filter(docType =>
                docType.persona === selectedPersona
            );
        }

        if (filteredDocTypes.length === 0) {
            return '<div class="empty-state-card">No document types available for this persona</div>';
        }

        const docTypeCards = filteredDocTypes.map((docType, index) =>
            `<div class="selection-card ${index === 0 ? 'selected' : ''}" data-value="${docType.id}">
                <div class="card-header">
                    <h4>${docType.isCustom ? '⭐ ' : ''}${docType.name}</h4>
                    <div class="card-actions">
                        ${docType.isCustom ? 
                            `<button class="btn-icon edit-doctype-btn" data-id="${docType.id}" title="Edit">✏️</button>` :
                            `<button class="btn-icon clone-doctype-btn" data-id="${docType.id}" title="Clone">📋</button>`
                        }
                    </div>
                </div>
                <div class="card-description">
                    ${docType.description || 'Professional document template'}
                </div>
            </div>`
        ).join('');

        // Add "Create Custom Type" card at the end
        const addCustomCard = `
            <div class="selection-card add-custom-card" data-action="add-custom-type" data-persona="${selectedPersona || ''}">
                <div class="card-header">
                    <h4>➕ Add Custom Type</h4>
                </div>
                <div class="card-description">
                    Create your own document type
                </div>
            </div>
        `;

        return docTypeCards + addCustomCard;
    }

    updateDocumentTypeCards(selectedPersona) {
        const documentTypeCardsContainer = document.getElementById('document-type-cards');
        if (documentTypeCardsContainer) {
            documentTypeCardsContainer.innerHTML = this.renderDocumentTypeCards(selectedPersona);
            // Validate form after updating cards
            if (this.validateForm) this.validateForm();
        }
    }

    getDefaultPersona() {
        // Use defaultPersona from settings, fallback to first persona
        if (this.config.settings && this.config.settings.defaultPersona) {
            return this.config.settings.defaultPersona;
        }

        // Fallback to first persona if no default set
        if (this.config.personas) {
            const personaKeys = Object.keys(this.config.personas);
            return personaKeys.length > 0 ? personaKeys[0] : null;
        }
        return null;
    }

    async loadProjects() {
        try {
            const response = await AuthManager.get('/api/projects');
            const projects = await response.json();

            const projectsList = document.getElementById('projects-list');

            if (projects.length === 0) {
                projectsList.innerHTML = `
                    <div class="empty-state">
                        <h3>No projects yet</h3>
                        <p>Create your first project to get started with document acceleration.</p>
                        <button class="btn btn-primary" onclick="document.getElementById('project-name')?.focus()">
                            Create Your First Project
                        </button>
                    </div>
                `;
            } else {
                projectsList.innerHTML = projects.map(project => `
                    <div class="project-card" data-project-id="${project.id}">
                        <div class="project-card-header">
                            <h3>${project.name}</h3>
                            <div class="project-meta">
                                <span class="persona-tag">${this.config.personas[project.persona]?.name || project.persona}</span>
                                <span class="document-type-tag">${this.config.documentTypes[project.documentType]?.name || project.documentType}</span>
                                <span class="files-count">${project.files.length} files</span>
                            </div>
                            <div class="project-actions">
                                <span class="project-date">${new Date(project.updatedAt).toLocaleDateString()}</span>
                                <button class="delete-project-btn" data-project-id="${project.id}" title="Delete project">🗑️</button>
                            </div>
                        </div>
                        <p class="project-description">${project.description || 'No description'}</p>
                    </div>
                `).join('');

                // Add click handlers
                document.querySelectorAll('.project-card').forEach((card, index) => {
                    card.addEventListener('click', (e) => {
                        // Don't open project if clicking on delete button
                        if (!e.target.classList.contains('delete-project-btn')) {
                            window.location.hash = `project/${projects[index].id}`;
                        }
                    });
                });

                // Add delete button handlers
                document.querySelectorAll('.delete-project-btn').forEach(btn => {
                    btn.addEventListener('click', (e) => {
                        e.stopPropagation();
                        const projectId = btn.dataset.projectId;
                        const project = projects.find(p => p.id === projectId);
                        this.deleteProject(project);
                    });
                });

                // Update project count in header
                this.updateProjectCount(projects.length);
            }
        } catch (error) {
            console.error('Failed to load projects:', error);
            document.getElementById('projects-list').innerHTML = `
                <div class="error-state">
                    <p>Failed to load projects. Please try refreshing the page.</p>
                </div>
            `;
            this.updateProjectCount(0);
        }
    }

    updateProjectCount(count) {
        const projectCountElement = document.getElementById('project-count');
        if (projectCountElement) {
            projectCountElement.textContent = count;
        }
    }

    getUserGreeting() {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (user.name) {
            // Extract first name from full name
            const firstName = user.name.split(',')[1]?.trim() || user.name.split(' ')[0] || user.name;
            return `Hi ${firstName}!<br>Create New Project`;
        }
        return 'Create New Project';
    }

    async deleteProject(project) {
        const confirmMessage = `Are you sure you want to delete "${project.name}"?\n\nThis will permanently delete:\n- All project files and versions\n- Chat history\n- Project metadata\n\nThis action cannot be undone.`;

        if (!confirm(confirmMessage)) {
            return;
        }

        try {
            const response = await AuthManager.delete(`/api/projects/${project.id}`);

            if (response.ok) {
                // Show success message
                this.showNotification(`Project "${project.name}" deleted successfully`, 'success');

                // Reload projects list
                await this.loadProjects();
            } else {
                const error = await response.json();
                throw new Error(error.error || 'Failed to delete project');
            }
        } catch (error) {
            console.error('Error deleting project:', error);
            this.showNotification(`Failed to delete project: ${error.message}`, 'error');
        }
    }

    showNotification(message, type = 'info') {
        const notification = document.createElement('div');
        notification.className = `notification ${type}`;
        notification.textContent = message;

        document.body.appendChild(notification);

        // Auto-remove after 3 seconds
        setTimeout(() => {
            if (notification.parentElement) {
                document.body.removeChild(notification);
            }
        }, 3000);
    }

    // Config utility methods
    getConfig(key, defaultValue = null) {
        try {
            const config = JSON.parse(localStorage.getItem('jet-stream-config') || '{}');
            return config[key] !== undefined ? config[key] : defaultValue;
        } catch (error) {
            console.error('Error reading config:', error);
            return defaultValue;
        }
    }

    setConfig(key, value) {
        try {
            const config = JSON.parse(localStorage.getItem('jet-stream-config') || '{}');
            config[key] = value;
            localStorage.setItem('jet-stream-config', JSON.stringify(config));
        } catch (error) {
            console.error('Error saving config:', error);
        }
    }

    checkAndShowFirstTimeHelp() {
        // Check if user has seen the workflow help before
        const hasSeenHelp = this.getConfig('hasSeenWorkflowHelp', false);
        if (!hasSeenHelp) {
            // Show help modal after a brief delay
            setTimeout(() => {
                this.showWorkflowHelpModal();
                this.setConfig('hasSeenWorkflowHelp', true);
            }, 1000);
        }
    }

    checkAndShowIntro() {
        // Check if user has dismissed the intro section
        const hasClosedIntro = this.getConfig('hasClosedIntro', false);
        const introSection = document.getElementById('home-intro-section');
        if (introSection) {
            if (hasClosedIntro) {
                introSection.style.display = 'none';
            } else {
                introSection.style.display = 'block';
            }
        }
    }

    closeIntroSection() {
        const introSection = document.getElementById('home-intro-section');
        if (introSection) {
            introSection.style.display = 'none';
            this.setConfig('hasClosedIntro', true);
        }
    }

    showWorkflowHelpModal() {
        const modal = document.createElement('div');
        modal.className = 'modal workflow-help-modal';
        modal.style.display = 'flex';

        modal.innerHTML = `
            <div class="modal-content workflow-help">
                <div class="modal-header">
                    <h3>Understanding Plan & Act Workflow</h3>
                    <button class="close-btn">&times;</button>
                </div>
                <div class="modal-body">
                    <div class="help-section">
                        <h4>Why Two Phases?</h4>
                        <p>Traditional AI document generation often produces <strong>incomplete or generic content</strong> because it lacks context. The Plan/Act workflow ensures comprehensive, high-quality documents by separating information gathering from document creation.</p>
                    </div>

                    <div class="workflow-diagram">
                        <div class="workflow-step">
                            <div class="step-icon plan-icon">📋</div>
                            <div class="step-content">
                                <h5>PLAN Mode</h5>
                                <p class="step-description">Gather Information</p>
                                <ul>
                                    <li>AI asks strategic questions</li>
                                    <li>Clarifies requirements and context</li>
                                    <li>Ensures nothing is missed</li>
                                    <li>Builds comprehensive understanding</li>
                                </ul>
                            </div>
                        </div>
                        
                        <div class="workflow-arrow">→</div>
                        
                        <div class="workflow-step">
                            <div class="step-icon act-icon">✍️</div>
                            <div class="step-content">
                                <h5>ACT Mode</h5>
                                <p class="step-description">Create Document</p>
                                <ul>
                                    <li>AI generates professional content</li>
                                    <li>Uses all gathered information</li>
                                    <li>Follows best practices</li>
                                    <li>Produces complete, polished documents</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div class="help-section example-section">
                        <h4>Example: Strategic Vision Document</h4>
                        <div class="example-comparison">
                            <div class="example-bad">
                                <h5>❌ Without Plan Mode</h5>
                                <p class="example-text">"Create a vision document for our cloud migration."</p>
                                <p class="example-result">Result: Generic document missing key stakeholders, timelines, constraints, and business drivers.</p>
                            </div>
                            <div class="example-good">
                                <h5>✅ With Plan/Act Workflow</h5>
                                <p class="example-text">AI asks about: business objectives, current state, stakeholders, constraints, success metrics, timeline...</p>
                                <p class="example-result">Result: Comprehensive, tailored document addressing your specific context and requirements.</p>
                            </div>
                        </div>
                    </div>

                    <div class="help-section">
                        <h4>Building AI Literacy</h4>
                        <p>This workflow teaches you to work <strong>with AI as a partner</strong>, not just a tool. By engaging in the planning conversation, you:</p>
                        <ul>
                            <li>Learn what information makes documents effective</li>
                            <li>Develop better prompting skills</li>
                            <li>Understand how to guide AI iteratively</li>
                            <li>Create a foundation for agentic collaboration</li>
                        </ul>
                    </div>

                    <div class="help-section tips-section">
                        <h4>💡 Quick Tips</h4>
                        <ul>
                            <li><strong>Start in PLAN mode:</strong> Let the AI guide you through what's needed</li>
                            <li><strong>Be thorough:</strong> The more context you provide, the better the output</li>
                            <li><strong>Switch to ACT:</strong> When you've answered all questions, switch modes to generate</li>
                            <li><strong>Iterate:</strong> You can always switch back to PLAN to add more context</li>
                        </ul>
                    </div>
                </div>
                <div class="modal-footer">
                    <button class="btn btn-primary close-help-btn">Got it!</button>
                </div>
            </div>
        `;

        document.body.appendChild(modal);
        document.body.style.overflow = 'hidden';

        // Close handlers
        const closeModal = () => {
            document.body.style.overflow = '';
            document.body.removeChild(modal);
        };

        modal.querySelector('.close-btn').addEventListener('click', closeModal);
        modal.querySelector('.close-help-btn').addEventListener('click', closeModal);
        
        // Close on backdrop click
        modal.addEventListener('click', (e) => {
            if (e.target === modal) {
                closeModal();
            }
        });
    }

    setupFormValidation() {
        const projectNameInput = document.getElementById('project-name');
        const createBtn = document.getElementById('create-project-btn');

        const validateForm = () => {
            const hasName = projectNameInput.value.trim().length > 0;
            const hasPersona = document.querySelector('#persona-cards .selection-card.selected');
            const hasDocType = document.querySelector('#document-type-cards .selection-card.selected');

            createBtn.disabled = !(hasName && hasPersona && hasDocType);
        };

        // Validate on input changes
        projectNameInput.addEventListener('input', validateForm);

        // Handle Enter key on project name input
        projectNameInput.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                if (!createBtn.disabled) {
                    createBtn.click();
                }
            }
        });

        // Store validation function for use in card selection
        this.validateForm = validateForm;
    }

    setupCardSelection() {
        // Handle persona card selection
        document.addEventListener('click', async (e) => {
            // Handle document type card action buttons FIRST (before card selection)
            const editBtn = e.target.closest('.edit-doctype-btn');
            const cloneBtn = e.target.closest('.clone-doctype-btn');
            
            if (editBtn || cloneBtn) {
                e.preventDefault();
                e.stopPropagation();
                
                const btn = editBtn || cloneBtn;
                const typeId = btn.dataset.id;
                
                const settingsManager = new SettingsManager();
                await settingsManager.loadConfiguration();
                
                if (editBtn) {
                    await settingsManager.showDocumentTypeEditor(typeId);
                } else {
                    await settingsManager.cloneDocumentType(typeId);
                }
                return;
            }

            // Handle "Add Custom Type" card
            const addCustomCard = e.target.closest('.add-custom-card');
            if (addCustomCard) {
                e.preventDefault();
                e.stopPropagation();
                
                const preSelectedPersona = addCustomCard.dataset.persona;
                const settingsManager = new SettingsManager();
                await settingsManager.loadConfiguration();
                
                // Pass the pre-selected persona to the editor
                await settingsManager.showDocumentTypeEditor(null, null, preSelectedPersona);
                return;
            }

            if (e.target.closest('#persona-cards .selection-card')) {
                const card = e.target.closest('.selection-card');
                // Remove selection from all persona cards
                document.querySelectorAll('#persona-cards .selection-card').forEach(c => c.classList.remove('selected'));
                // Add selection to clicked card
                card.classList.add('selected');

                // Get selected persona and update document types
                const selectedPersona = card.dataset.value;
                this.updateDocumentTypeCards(selectedPersona);

                // Validate form
                if (this.validateForm) this.validateForm();
            }

            // Handle document type card selection
            if (e.target.closest('#document-type-cards .selection-card')) {
                const card = e.target.closest('.selection-card');
                
                // Don't select the "Add Custom Type" card
                if (card.classList.contains('add-custom-card')) {
                    return;
                }
                
                // Remove selection from all document type cards
                document.querySelectorAll('#document-type-cards .selection-card').forEach(c => c.classList.remove('selected'));
                // Add selection to clicked card
                card.classList.add('selected');
                // Validate form
                if (this.validateForm) this.validateForm();
            }
        });
    }

    setupHomePageEvents() {
        // Settings button
        document.getElementById('settings-btn').addEventListener('click', () => {
            const settingsManager = new SettingsManager();
            settingsManager.showSettingsModal();
        });

        // Create project button
        document.getElementById('create-project-btn').addEventListener('click', async () => {
            await this.createProject();
        });

        // Close intro section button
        const closeIntroBtn = document.getElementById('close-intro-btn');
        if (closeIntroBtn) {
            closeIntroBtn.addEventListener('click', () => {
                this.closeIntroSection();
            });
        }

        // Check and show/hide intro section based on config
        this.checkAndShowIntro();

        // Setup card selection handlers and form validation
        this.setupCardSelection();
        this.setupFormValidation();
    }

    async createProject() {
        try {
            const selectedPersona = document.querySelector('#persona-cards .selection-card.selected');
            const selectedDocType = document.querySelector('#document-type-cards .selection-card.selected');

            if (!selectedPersona) {
                alert('Please select a persona');
                return;
            }

            if (!selectedDocType) {
                alert('Please select a document type');
                return;
            }

            const formData = {
                name: document.getElementById('project-name').value,
                description: document.getElementById('project-description').value,
                persona: selectedPersona.dataset.value,
                documentType: selectedDocType.dataset.value
            };

            const response = await AuthManager.post('/api/projects', formData);

            if (response.ok) {
                const project = await response.json();
                // Clear the form
                document.getElementById('project-name').value = '';
                document.getElementById('project-description').value = '';
                document.querySelectorAll('.selection-card.selected').forEach(card => card.classList.remove('selected'));
                document.getElementById('create-project-btn').disabled = true;

                // Navigate to the new project
                window.location.hash = `project/${project.id}`;
            } else {
                const error = await response.json();
                alert(`Failed to create project: ${error.error}`);
            }
        } catch (error) {
            console.error('Error creating project:', error);
            alert('Failed to create project. Please try again.');
        }
    }

    async renderProjectPage(container) {
        try {
            // Load project data
            const response = await AuthManager.get(`/api/projects/${this.currentProject}`);
            if (!response.ok) {
                throw new Error('Project not found');
            }

            const project = await response.json();

            // Reset file state when switching projects
            this.currentOpenFile = null;
            this.currentFileContent = null;
            this.isVersionView = false;

            // Initialize excluded files from project config
            this.excludedFromContext = new Set(project.excludedFromContext || []);

            // Initialize file manager
            this.fileManager = new FileManager(this.currentProject);

            container.innerHTML = `
                <div class="project-page">
                    <header class="project-header">
                        <a href="#" class="back-link">← Projects</a>
                        <h1 class="project-name">${project.name}</h1>
                        <div class="project-meta">
                            <span class="persona-tag">${this.config.personas[project.persona]?.name || project.persona}</span>
                            <span class="document-type-tag">${this.config.documentTypes[project.documentType]?.name || project.documentType}</span>
                            <button id="workflow-help-btn" class="action-btn" title="Learn about Plan/Act workflow">?</button>
                            <button id="refresh-files-btn" class="action-btn" title="Refresh and sync files">🔄</button>
                        </div>
                    </header>

                    <div class="project-main">
                        <div class="chat-panel">
                            <div id="chat-messages" class="chat-messages">
                                <div class="loading">Loading conversation...</div>
                            </div>
                            <div class="chat-input-container">
                                <div class="chat-input-wrapper">
                                    <textarea id="chat-input" placeholder="Type your message..." rows="3"></textarea>
                                    <div class="chat-input-actions">
                                        <button id="mode-toggle-btn" class="action-btn mode-toggle plan-mode" title="Click to switch mode">
                                            <span class="mode-label">PLAN</span>
                                        </button>
                                        <select id="model-selector" class="model-selector" title="Select AI model">
                                            <option value="claude-3.7-sonnet" selected>Sonnet 3.7</option>
                                        </select>
                                        <button id="upload-btn" class="action-btn" title="Upload file">📎</button>
                                        <button id="send-btn" class="action-btn primary" disabled>Send</button>
                                    </div>
                                </div>
                                <input type="file" id="file-input" style="display: none;" accept=".md,.txt,.pdf,.docx">
                            </div>
                        </div>

                        <div class="files-panel">
                            <div class="files-content">
                                <div class="files-sidebar">
                                    <div id="files-list" class="files-list">
                                        <div class="loading">Loading files...</div>
                                    </div>
                                </div>
                                <div class="editor-area">
                                    <div class="editor-container">
                                        <div class="editor-header">
                                            <span id="current-file-name">No file selected</span>
                                            <div class="editor-actions">
                                                <button id="toggle-editor-mode-btn" class="action-btn" title="Toggle editor mode">Markdown</button>
                                                <button id="save-file-btn" class="action-btn">Save</button>
                                                <button id="export-pdf-btn" class="action-btn">Export PDF</button>
                                                <button id="close-editor-btn" class="action-btn">Close</button>
                                            </div>
                                        </div>
                                        <div id="editor" class="editor">
                                            <!-- Milkdown editor will be embedded here -->
                                        </div>
                                        <textarea id="markdown-editor" class="markdown-textarea" style="display: none;"></textarea>
                                    </div>
                                    <div class="no-file-selected" id="no-file-selected">
                                        <div class="no-file-message">
                                            <h3>No file selected</h3>
                                            <p>Select a file from the sidebar to start editing</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            `;

            // Project values are now display-only in the header

            // Load chat session and files
            await this.loadChatSession();
            await this.loadProjectFiles();

            // Setup event listeners
            this.setupProjectPageEvents();

        } catch (error) {
            console.error('Error loading project:', error);
            container.innerHTML = `
                <div class="project-page">
                    <div class="error-state">
                        <h2>Project Not Found</h2>
                        <p>The requested project could not be loaded.</p>
                        <a href="#" class="btn btn-primary">← Back to Projects</a>
                    </div>
                </div>
            `;
        }
    }

    async loadChatSession() {
        try {
            const response = await AuthManager.get(`/api/projects/${this.currentProject}/chat`);
            const chatSession = await response.json();

            this.renderChatMessages(chatSession.messages);
            this.updateModeUI(chatSession.context);

            // Store current session context
            this.currentSessionContext = chatSession.context;

        } catch (error) {
            console.error('Error loading chat session:', error);
            document.getElementById('chat-messages').innerHTML = `
                <div class="error-message">Failed to load conversation history</div>
            `;
        }
    }

    renderChatMessages(messages) {
        const chatMessages = document.getElementById('chat-messages');

        if (messages.length === 0) {
            chatMessages.innerHTML = `
                <div class="empty-chat">
                    <p>Start a conversation to begin creating your document.</p>
                </div>
            `;
            return;
        }

        chatMessages.innerHTML = messages.map(message => `
            <div class="message ${message.role}">
                <div class="message-content">
                    ${this.formatMessageContent(message.content)}
                </div>
                <div class="message-meta">
                    <span class="message-time">${new Date(message.timestamp).toLocaleTimeString()}</span>
                    ${message.metadata?.mode ? `<span class="message-mode">${message.metadata.mode.toUpperCase()}</span>` : ''}
                </div>
            </div>
        `).join('');

        // Scroll to bottom after layout is complete
        setTimeout(() => {
            chatMessages.scrollTop = chatMessages.scrollHeight;
        }, 0);
    }

    formatMessageContent(content) {
        // Simple markdown-like formatting
        return content
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.*?)\*/g, '<em>$1</em>')
            .replace(/\n/g, '<br>');
    }

    updateModeUI(context) {
        console.log('updateModeUI called with:', context);
        const modeToggleBtn = document.getElementById('mode-toggle-btn');

        if (!modeToggleBtn) {
            console.error('Mode toggle button not found in DOM');
            return;
        }

        // Update mode toggle button
        const modeLabel = modeToggleBtn.querySelector('.mode-label');

        if (context.mode === 'plan') {
            console.log('Setting mode to PLAN');
            modeToggleBtn.className = 'action-btn mode-toggle plan-mode';
            modeLabel.textContent = 'PLAN';
            modeToggleBtn.title = 'Switch to ACT mode';
        } else {
            console.log('Setting mode to ACT');
            modeToggleBtn.className = 'action-btn mode-toggle act-mode';
            modeLabel.textContent = 'ACT';
            modeToggleBtn.title = 'Switch to PLAN mode';
        }
    }

    async loadProjectFiles() {
        try {
            const files = await this.fileManager.loadFiles();
            this.renderFilesList(files);

            // If a file is currently open, reload it to get latest content
            if (this.currentOpenFile) {
                console.log('Reloading currently open file:', this.currentOpenFile);
                await this.reloadCurrentFile();
            }
        } catch (error) {
            console.error('Error loading files:', error);
            document.getElementById('files-list').innerHTML = `
                <div class="error-message">Failed to load files</div>
            `;
        }
    }

    async reloadCurrentFile() {
        if (!this.currentOpenFile) {
            return;
        }

        try {
            console.log('Fetching latest content for:', this.currentOpenFile);

            // Fetch the latest content
            const response = await AuthManager.get(`/api/projects/${this.currentProject}/files/${this.currentOpenFile}`);

            if (!response.ok) {
                console.error('Failed to reload file:', response.statusText);
                return;
            }

            const fileData = await response.json();
            console.log('Reloaded file content, length:', fileData.content ? fileData.content.length : 0);

            // Update the editor with new content
            if (this.milkdownEditor) {
                console.log('Updating Milkdown editor with new content');
                this.milkdownEditor.setContent(fileData.content);
            }

            // Also update markdown textarea if it's visible
            const markdownTextarea = document.getElementById('markdown-editor');
            if (markdownTextarea && markdownTextarea.style.display !== 'none') {
                console.log('Updating markdown textarea with new content');
                markdownTextarea.value = fileData.content;
            }

            // Store the updated content
            this.currentFileContent = fileData.content;

        } catch (error) {
            console.error('Error reloading current file:', error);
        }
    }

    async syncProjectFiles() {
        try {
            // Show loading state
            const syncBtn = document.getElementById('sync-files-btn');
            const originalTitle = syncBtn.title;
            syncBtn.disabled = true;
            syncBtn.title = 'Syncing...';
            syncBtn.textContent = '⏳';

            // Call sync endpoint
            const response = await AuthManager.post(`/api/projects/${this.currentProject}/files/sync`, {});

            if (response.ok) {
                const result = await response.json();

                // Show success feedback
                if (result.syncedFiles.length > 0) {
                    this.showSyncSuccess(result.syncedFiles);
                } else {
                    this.showSyncInfo('All files are already synced');
                }

                // Refresh the files list
                await this.loadProjectFiles();
            } else {
                throw new Error('Sync failed');
            }
        } catch (error) {
            console.error('Error syncing files:', error);
            this.showSyncError('Failed to sync files. Please try again.');
        } finally {
            // Reset button state
            const syncBtn = document.getElementById('sync-files-btn');
            syncBtn.disabled = false;
            syncBtn.title = 'Sync files from disk';
            syncBtn.textContent = '🔗';
        }
    }

    showSyncSuccess(syncedFiles) {
        const notification = document.createElement('div');
        notification.className = 'sync-notification success';
        notification.innerHTML = `
            <strong>Files Synced Successfully!</strong>
            <p>Added ${syncedFiles.length} file(s): ${syncedFiles.join(', ')}</p>
        `;

        document.body.appendChild(notification);

        setTimeout(() => {
            if (notification.parentElement) {
                document.body.removeChild(notification);
            }
        }, 5000);
    }

    showSyncInfo(message) {
        const notification = document.createElement('div');
        notification.className = 'sync-notification info';
        notification.innerHTML = `
            <strong>Sync Complete</strong>
            <p>${message}</p>
        `;

        document.body.appendChild(notification);

        setTimeout(() => {
            if (notification.parentElement) {
                document.body.removeChild(notification);
            }
        }, 3000);
    }

    showSyncError(message) {
        const notification = document.createElement('div');
        notification.className = 'sync-notification error';
        notification.innerHTML = `
            <strong>Sync Failed</strong>
            <p>${message}</p>
        `;

        document.body.appendChild(notification);

        setTimeout(() => {
            if (notification.parentElement) {
                document.body.removeChild(notification);
            }
        }, 5000);
    }

    async refreshAndSyncFiles() {
        try {
            // Show loading state
            const refreshBtn = document.getElementById('refresh-files-btn');
            const originalTitle = refreshBtn.title;
            refreshBtn.disabled = true;
            refreshBtn.title = 'Syncing...';
            refreshBtn.textContent = '⏳';

            // Call sync endpoint
            const response = await AuthManager.post(`/api/projects/${this.currentProject}/files/sync`, {});

            if (response.ok) {
                const result = await response.json();

                // Show success feedback only if files were synced
                if (result.syncedFiles.length > 0) {
                    this.showSyncSuccess(result.syncedFiles);
                }

                // Refresh the files list
                await this.loadProjectFiles();
            } else {
                throw new Error('Sync failed');
            }
        } catch (error) {
            console.error('Error syncing files:', error);
            this.showSyncError('Failed to sync files. Please try again.');
        } finally {
            // Reset button state
            const refreshBtn = document.getElementById('refresh-files-btn');
            refreshBtn.disabled = false;
            refreshBtn.title = 'Refresh and sync files';
            refreshBtn.textContent = '🔄';
        }
    }

    async handlePostResponseFileSync(sessionContext) {
        try {
            console.log('Post-response file sync started...');

            // Store currently open file
            const currentlyOpenFile = this.currentOpenFile;
            console.log('Currently open file:', currentlyOpenFile);

            // Get the current file list before sync
            const oldFiles = await this.fileManager.loadFiles();
            const oldFileNames = new Set(oldFiles.map(f => f.name));

            // Sync files from filesystem
            const syncResponse = await AuthManager.post(`/api/projects/${this.currentProject}/files/sync`, {});

            if (syncResponse.ok) {
                const syncResult = await syncResponse.json();
                console.log('Sync result:', syncResult);

                // Refresh the files list
                await this.loadProjectFiles();

                // Get the updated file list
                const newFiles = await this.fileManager.loadFiles();

                // Find newly created files
                const newFileNames = newFiles
                    .filter(f => !oldFileNames.has(f.name))
                    .map(f => f.name);

                // Find files with new versions (check addedVersions from sync result)
                const updatedFiles = syncResult.addedVersions || [];
                const updatedFileNames = updatedFiles.map(v => v.split(' v')[0]);

                // Check if currently open file has a new version
                if (currentlyOpenFile && updatedFileNames.includes(currentlyOpenFile)) {
                    console.log('Currently open file has new version, reloading:', currentlyOpenFile);
                    await this.openFile(currentlyOpenFile);
                    return; // Don't open a different file
                }

                // If new files were created, open the first one
                if (newFileNames.length > 0) {
                    const fileToOpen = newFileNames[0];
                    console.log('Opening newly created file:', fileToOpen);
                    await this.openFile(fileToOpen);
                    return;
                }

                // If files were synced and no file is open, open the first synced file
                if (!currentlyOpenFile && syncResult.syncedFiles && syncResult.syncedFiles.length > 0) {
                    const fileToOpen = syncResult.syncedFiles[0];
                    console.log('Opening synced file:', fileToOpen);
                    await this.openFile(fileToOpen);
                }
            }
        } catch (error) {
            console.error('Error handling post-response file sync:', error);
            // Don't show error to user, just log it
        }
    }

    renderFilesList(files) {
        const filesList = document.getElementById('files-list');

        if (files.length === 0) {
            filesList.innerHTML = `
                <div class="empty-files">
                    <div class="file-drop-zone" id="file-drop-zone">
                        <p>No files yet. Drag and drop files here to get started.</p>
                    </div>
                </div>
            `;
            this.setupFileDropZone();
        } else {
            filesList.innerHTML = `
                <div class="files-container">
                    ${files.map(file => this.renderFileItem(file)).join('')}
                </div>
            `;

            this.setupFileItemEvents();
        }

        // Ensure proper initial state
        if (!this.currentOpenFile) {
            document.querySelector('.editor-container').style.display = 'none';
            document.getElementById('no-file-selected').style.display = 'flex';
        }
    }

    renderFileItem(file) {
        const fileType = file.name.split('.').pop() || 'unknown';
        const modifiedDate = file.modified ? new Date(file.modified).toLocaleDateString() : 'Unknown';
        const isExcluded = this.excludedFromContext.has(file.name);
        const isBinaryFile = fileType === 'pdf';

        return `
            <div class="file-item ${isExcluded ? 'excluded-from-context' : ''}" data-filename="${file.name}">
                <div class="file-info">
                    <span class="file-name">
                        ${file.name}
                    </span>
                    <div class="file-meta">
                        <span class="file-size">${this.formatFileSize(file.size)}</span>
                        <span class="file-modified">Modified: ${modifiedDate}</span>
                        <div class="file-actions">
                            <button class="file-context-btn ${isExcluded ? 'excluded' : 'included'}" data-filename="${file.name}" data-action="toggle-context" title="${isExcluded ? 'Click to include in context' : 'Click to exclude from context'}">
                                <span class="context-icon">👁️</span>
                                <span class="context-label">${isExcluded ? 'Excluded' : 'In Context'}</span>
                            </button>
                            ${isBinaryFile ?
                `<button class="file-delete-btn" data-filename="${file.name}" data-action="delete" title="Delete file">🗑️</button>` :
                `<span class="file-versions">Versions: ${file.versions || 1}</span>
                                 <button class="file-action-btn" data-filename="${file.name}" data-action="versions" title="View versions">↩️</button>`
            }
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    updateFileSelection(selectedFilename) {
        // Remove selection from all files
        document.querySelectorAll('.file-item').forEach(item => {
            item.classList.remove('selected');
        });

        // Add selection to the specified file
        if (selectedFilename) {
            const selectedItem = document.querySelector(`[data-filename="${selectedFilename}"]`);
            if (selectedItem) {
                selectedItem.classList.add('selected');
            }
        }
    }

    setupFileItemEvents() {
        // Remove any existing event listeners
        const filesContainer = document.querySelector('.files-container');
        if (!filesContainer) return;

        // Use event delegation on the files container
        filesContainer.addEventListener('click', async (e) => {
            console.log('Click detected:', e.target, 'classes:', e.target.className);

            // Check if clicked element is a context toggle button
            if (e.target.closest('.file-context-btn')) {
                e.preventDefault();
                e.stopPropagation();
                const btn = e.target.closest('.file-context-btn');
                const filename = btn.dataset.filename;
                this.toggleFileContext(filename, btn);
                return;
            }

            // Check if clicked element is an action button
            if (e.target.classList.contains('file-action-btn')) {
                e.preventDefault();
                e.stopPropagation();
                console.log('Action button clicked:', e.target.dataset.action, e.target.dataset.filename);

                const filename = e.target.dataset.filename;
                const action = e.target.dataset.action;

                switch (action) {
                    case 'versions':
                        this.showFileVersions(filename);
                        break;
                    default:
                        this.openFile(filename);
                }
                return;
            }

            // Check if clicked element is a delete button
            if (e.target.classList.contains('file-delete-btn')) {
                e.preventDefault();
                e.stopPropagation();
                const filename = e.target.dataset.filename;

                // Show confirmation dialog
                const confirmMessage = `Are you sure you want to delete "${filename}"?\n\nThis action cannot be undone.`;
                if (confirm(confirmMessage)) {
                    try {
                        await this.deleteFile(filename);
                        await this.refreshAndSyncFiles(); // Refresh and sync files
                        this.showNotification(`File "${filename}" deleted successfully`, 'success');
                    } catch (error) {
                        console.error('Error deleting file:', error);
                        this.showNotification('Failed to delete file. Please try again.', 'error');
                    }
                }
                return;
            }

            // Find the closest file item
            const fileItem = e.target.closest('.file-item');
            if (fileItem && !e.target.closest('.file-action-btn') && !e.target.closest('.file-delete-btn')) {
                const filename = fileItem.dataset.filename;
                console.log('Opening file:', filename);
                this.openFile(filename);
            }
        });
    }

    async toggleFileContext(filename, btn) {
        const label = btn.querySelector('.context-label');
        const fileItem = btn.closest('.file-item');

        if (this.excludedFromContext.has(filename)) {
            // Include in context
            this.excludedFromContext.delete(filename);
            btn.classList.add('included');
            btn.classList.remove('excluded');
            btn.title = 'Click to exclude from context';
            if (label) label.textContent = 'In Context';
            if (fileItem) fileItem.classList.remove('excluded-from-context');
            console.log(`File ${filename} included in context`);
        } else {
            // Exclude from context
            this.excludedFromContext.add(filename);
            btn.classList.remove('included');
            btn.classList.add('excluded');
            btn.title = 'Click to include in context';
            if (label) label.textContent = 'Excluded';
            if (fileItem) fileItem.classList.add('excluded-from-context');
            console.log(`File ${filename} excluded from context`);
        }

        // Save to project config
        await this.saveExcludedFilesToProject();
    }

    async saveExcludedFilesToProject() {
        if (!this.currentProject) return;

        try {
            const response = await AuthManager.put(`/api/projects/${this.currentProject}`, {
                excludedFromContext: Array.from(this.excludedFromContext)
            });

            if (!response.ok) {
                throw new Error('Failed to save excluded files');
            }

            console.log('Excluded files saved to project');
        } catch (error) {
            console.error('Error saving excluded files:', error);
        }
    }

    setupFileDropZone() {
        const dropZone = document.getElementById('file-drop-zone');
        if (!dropZone) return;

        dropZone.addEventListener('dragover', (e) => {
            e.preventDefault();
            dropZone.classList.add('drag-over');
        });

        dropZone.addEventListener('dragleave', (e) => {
            e.preventDefault();
            dropZone.classList.remove('drag-over');
        });

        dropZone.addEventListener('drop', (e) => {
            e.preventDefault();
            dropZone.classList.remove('drag-over');

            const files = Array.from(e.dataTransfer.files);
            files.forEach(file => this.uploadFile(file));
        });
    }

    formatFileSize(bytes) {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    }

    setupProjectPageEvents() {
        // Back to home
        document.querySelector('.back-link').addEventListener('click', (e) => {
            e.preventDefault();
            window.location.href = '/';
        });

        // Mode switching
        document.getElementById('mode-toggle-btn').addEventListener('click', () => {
            const modeBtn = document.getElementById('mode-toggle-btn');
            const currentMode = modeBtn.classList.contains('plan-mode') ? 'plan' : 'act';
            const newMode = currentMode === 'plan' ? 'act' : 'plan';
            this.switchMode(newMode);
        });

        // Chat input
        const chatInput = document.getElementById('chat-input');
        const sendBtn = document.getElementById('send-btn');

        chatInput.addEventListener('input', () => {
            sendBtn.disabled = !chatInput.value.trim();
        });

        chatInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (!sendBtn.disabled) {
                    this.sendMessage();
                }
            }
        });

        sendBtn.addEventListener('click', () => {
            this.sendMessage();
        });

        // Auto-focus chat input
        chatInput.focus();

        // File upload
        document.getElementById('upload-btn').addEventListener('click', () => {
            document.getElementById('file-input').click();
        });

        document.getElementById('file-input').addEventListener('change', (e) => {
            if (e.target.files.length > 0) {
                this.uploadFile(e.target.files[0]);
            }
        });

        // File management controls
        document.getElementById('refresh-files-btn').addEventListener('click', () => {
            this.refreshAndSyncFiles();
        });

        // Workflow help button
        document.getElementById('workflow-help-btn').addEventListener('click', () => {
            this.showWorkflowHelpModal();
        });

        // Show workflow help on first visit
        this.checkAndShowFirstTimeHelp();

        // Persona and document type are now display-only

        // Editor controls
        document.getElementById('toggle-editor-mode-btn').addEventListener('click', () => {
            this.toggleEditorMode();
        });

        // Markdown textarea change detection
        document.getElementById('markdown-editor').addEventListener('input', () => {
            this.markFileAsModified();
        });

        document.getElementById('save-file-btn').addEventListener('click', () => {
            this.saveCurrentFile();
        });

        document.getElementById('export-pdf-btn').addEventListener('click', () => {
            this.exportCurrentFileToPDF();
        });

        document.getElementById('close-editor-btn').addEventListener('click', () => {
            this.closeEditor();
        });
    }

    async openFile(filename) {
        try {
            console.log(`Opening file: ${filename} for project: ${this.currentProject}`);

            // Determine file type (DOCX files are converted to PDF on upload)
            const fileExtension = filename.toLowerCase().split('.').pop();
            const isBinaryFile = fileExtension === 'pdf';

            // Load file content - add explicit format parameter to ensure JSON response
            const response = await AuthManager.fetch(`/api/projects/${this.currentProject}/files/${filename}?format=json`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json'
                }
            });
            console.log(`File API response status: ${response.status}`);

            if (!response.ok) {
                const errorData = await response.json();
                console.error('File API error:', errorData);
                throw new Error(`Failed to load file: ${errorData.error || response.statusText}`);
            }

            const fileData = await response.json();
            console.log(`File data loaded, ${isBinaryFile ? 'binary file' : 'content length: ' + (fileData.content ? fileData.content.length : 0)}`);

            // Update UI to show editor
            document.getElementById('current-file-name').textContent = filename;
            document.getElementById('no-file-selected').style.display = 'none';
            document.querySelector('.editor-container').style.display = 'flex';

            // Update file selection in sidebar
            this.updateFileSelection(filename);

            this.currentOpenFile = filename;
            this.currentFileVersion = null;
            this.isVersionView = false;

            // Handle different file types
            if (isBinaryFile) {
                await this.initializeBinaryViewer(filename, fileExtension, fileData.content);

                // For binary files: only show close button in editor actions
                const editorActions = document.querySelector('.editor-actions');
                if (editorActions) {
                    editorActions.innerHTML = `
                        <button id="close-file-btn" class="action-btn">Close</button>
                    `;

                    // Add close event listener
                    document.getElementById('close-file-btn').addEventListener('click', () => {
                        this.closeCurrentFile();
                    });
                }

            } else {
                // Text files - use existing editor logic

                // Reset editor view to Milkdown mode
                const milkdownEditor = document.getElementById('editor');
                const markdownTextarea = document.getElementById('markdown-editor');

                milkdownEditor.style.display = 'block';
                markdownTextarea.style.display = 'none';
                markdownTextarea.value = '';

                // For text files: show toggle and save buttons in editor actions
                const editorActions = document.querySelector('.editor-actions');
                if (editorActions) {
                    editorActions.innerHTML = `
                        <button id="toggle-editor-mode-btn" class="action-btn" title="Toggle editor mode">Markdown</button>
                        <button id="save-file-btn" class="action-btn">Save</button>
                        <button id="export-pdf-btn" class="action-btn">Export PDF</button>
                        <button id="close-file-btn" class="action-btn">Close</button>
                    `;

                    // Add event listeners
                    document.getElementById('toggle-editor-mode-btn').addEventListener('click', () => {
                        this.toggleEditorMode();
                    });
                    document.getElementById('save-file-btn').addEventListener('click', () => {
                        this.saveCurrentFile();
                    });
                    document.getElementById('export-pdf-btn').addEventListener('click', () => {
                        this.exportCurrentFileToPDF();
                    });
                    document.getElementById('close-file-btn').addEventListener('click', () => {
                        this.closeCurrentFile();
                    });
                }

                // Initialize StackEdit.js
                console.log('Initializing StackEdit...');
                this.initializeStackEdit(fileData.content, filename);
            }

            console.log(`File opened successfully: ${filename}`);

        } catch (error) {
            console.error('Error opening file:', error);
            alert(`Failed to open file: ${error.message}`);
        }
    }

    async initializeBinaryViewer(filename, fileExtension, base64Content) {
        const container = document.getElementById('editor');
        container.innerHTML = '';

        // All binary files are now PDFs (DOCX converted on upload)
        if (fileExtension === 'pdf') {
            // Create PDF viewer using browser's built-in PDF viewer
            const pdfBlob = this.base64ToBlob(base64Content, 'application/pdf');
            const pdfUrl = URL.createObjectURL(pdfBlob);

            const embed = document.createElement('embed');
            embed.src = pdfUrl;
            embed.type = 'application/pdf';
            embed.style.width = '100%';
            embed.style.height = '100%';
            embed.style.border = 'none';

            container.appendChild(embed);

            // Clean up URL when file is closed
            this.currentBinaryUrl = pdfUrl;
        } else {
            // Fallback for unsupported binary files
            container.innerHTML = `
                <div style="padding: 20px; text-align: center; color: #666;">
                    <h3>Unsupported file type</h3>
                    <p>File: ${filename}</p>
                    <p>Type: ${fileExtension}</p>
                </div>
            `;
        }
    }

    base64ToBlob(base64, mimeType) {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: mimeType });
    }

    closeCurrentFile() {
        // Clean up binary URL if exists
        if (this.currentBinaryUrl) {
            URL.revokeObjectURL(this.currentBinaryUrl);
            this.currentBinaryUrl = null;
        }

        // Reset UI
        this.currentOpenFile = null;
        this.currentFileContent = null;
        this.currentFileVersion = null;
        this.isVersionView = false;

        document.getElementById('no-file-selected').style.display = 'flex';
        document.querySelector('.editor-container').style.display = 'none';

        // Clear file selection
        this.updateFileSelection(null);
    }

    async initializeStackEdit(content, filename, readOnly = false) {
        const container = document.getElementById('editor');

        // Clear previous editor
        container.innerHTML = '';

        // Destroy previous Milkdown instance if exists
        if (this.milkdownEditor) {
            this.milkdownEditor.destroy();
        }

        // Try to use Milkdown, fallback to simple editor on error
        try {
            console.log('Initializing Milkdown editor', readOnly ? '(read-only)' : '');

            // Create editor container
            const editorDiv = document.createElement('div');
            editorDiv.id = 'milkdown-editor';
            editorDiv.className = readOnly ? 'milkdown-readonly' : '';
            container.appendChild(editorDiv);

            // Initialize Milkdown
            this.milkdownEditor = new MilkdownEditorManager();
            await this.milkdownEditor.initialize(editorDiv, content, (markdown) => {
                this.currentFileContent = markdown;
                if (!readOnly) {
                    this.markFileAsModified();
                }
            });

            // Set read-only mode if needed
            if (readOnly) {
                this.milkdownEditor.setReadOnly(true);
            }

            this.currentFileContent = content;
            return;
        } catch (error) {
            console.error('Failed to initialize Milkdown, falling back to simple editor:', error);
        }

        // Fallback to simple editor
        this.initializeSimpleEditor(content, filename, container, readOnly);
    }

    initializeSimpleEditor(content, filename, container, readOnly = false) {
        console.log('Initializing simple markdown editor', readOnly ? '(read-only)' : '');

        const readOnlyAttr = readOnly ? 'readonly' : '';
        const placeholder = readOnly ? 'Viewing read-only version...' : 'Start writing your markdown content...';

        container.innerHTML = `
            <div class="simple-editor-wrapper ${readOnly ? 'read-only' : ''}">
                <textarea id="simple-editor" placeholder="${placeholder}" ${readOnlyAttr}>${content}</textarea>
                <div class="editor-status">
                    <span class="word-count">Words: 0</span>
                    <span class="char-count">Characters: 0</span>
                    <span class="line-count">Lines: 1</span>
                    ${readOnly ? '<span class="read-only-indicator">READ ONLY</span>' : ''}
                </div>
            </div>
        `;

        const textarea = container.querySelector('#simple-editor');
        const wordCount = container.querySelector('.word-count');
        const charCount = container.querySelector('.char-count');
        const lineCount = container.querySelector('.line-count');

        // Update counts
        const updateCounts = () => {
            const text = textarea.value;
            const words = text.trim() ? text.trim().split(/\s+/).length : 0;
            const chars = text.length;
            const lines = text.split('\n').length;
            wordCount.textContent = `Words: ${words}`;
            charCount.textContent = `Characters: ${chars}`;
            lineCount.textContent = `Lines: ${lines}`;
        };

        // Handle content changes (only if not read-only)
        if (!readOnly) {
            textarea.addEventListener('input', () => {
                this.currentFileContent = textarea.value;
                this.markFileAsModified();
                updateCounts();
            });
        }

        // Initialize counts
        updateCounts();

        // Store reference for saving
        this.currentFileContent = content;
        this.simpleEditor = textarea;
    }

    initializeFallbackEditor(content, filename, container) {
        console.log('Using simple textarea editor');

        container.innerHTML = `
            <div class="simple-editor-wrapper">
                <textarea id="simple-editor" placeholder="Start writing your markdown content...">${content}</textarea>
                <div class="editor-status">
                    <span class="word-count">Words: 0</span>
                    <span class="char-count">Characters: 0</span>
                    <span class="line-count">Lines: 1</span>
                </div>
            </div>
        `;

        const textarea = container.querySelector('#simple-editor');
        const wordCount = container.querySelector('.word-count');
        const charCount = container.querySelector('.char-count');
        const lineCount = container.querySelector('.line-count');

        // Update counts
        const updateCounts = () => {
            const text = textarea.value;
            const words = text.trim() ? text.trim().split(/\s+/).length : 0;
            const chars = text.length;
            const lines = text.split('\n').length;
            wordCount.textContent = `Words: ${words}`;
            charCount.textContent = `Characters: ${chars}`;
            lineCount.textContent = `Lines: ${lines}`;
        };

        // Handle content changes
        textarea.addEventListener('input', () => {
            this.currentFileContent = textarea.value;
            this.markFileAsModified();
            updateCounts();
        });

        // Initialize counts
        updateCounts();

        // Store reference for saving
        this.currentFileContent = content;
        this.simpleEditor = textarea;
    }

    markFileAsModified() {
        const saveBtn = document.getElementById('save-file-btn');
        if (!saveBtn.classList.contains('modified')) {
            saveBtn.classList.add('modified');
            saveBtn.textContent = 'Save*';
        }
    }

    toggleEditorMode() {
        const milkdownEditor = document.getElementById('editor');
        const markdownTextarea = document.getElementById('markdown-editor');
        const toggleBtn = document.getElementById('toggle-editor-mode-btn');

        if (!this.currentOpenFile) {
            return;
        }

        // Check current mode
        const isMarkdownMode = markdownTextarea.style.display !== 'none';

        if (isMarkdownMode) {
            // Switch to Milkdown editor
            const content = markdownTextarea.value;
            markdownTextarea.style.display = 'none';
            milkdownEditor.style.display = 'block';

            // Update Milkdown with the content
            if (this.milkdownEditor) {
                this.milkdownEditor.setContent(content);
            }

            toggleBtn.textContent = 'Markdown';
            toggleBtn.title = 'Switch to markdown mode';
        } else {
            // Switch to markdown textarea
            let content = '';
            if (this.milkdownEditor) {
                content = this.milkdownEditor.getContent();
            }

            milkdownEditor.style.display = 'none';
            markdownTextarea.style.display = 'block';
            markdownTextarea.value = content;

            toggleBtn.textContent = 'Editor';
            toggleBtn.title = 'Switch to editor mode';
        }
    }

    async saveCurrentFile() {
        if (!this.currentOpenFile) {
            return;
        }

        // Get the latest content from the active editor
        const markdownTextarea = document.getElementById('markdown-editor');
        const isMarkdownMode = markdownTextarea.style.display !== 'none';

        if (isMarkdownMode) {
            this.currentFileContent = markdownTextarea.value;
        } else if (this.milkdownEditor) {
            this.currentFileContent = this.milkdownEditor.getContent();
        }

        if (!this.currentFileContent) {
            return;
        }

        try {
            const response = await AuthManager.put(`/api/projects/${this.currentProject}/files/${this.currentOpenFile}`, {
                content: this.currentFileContent,
                createdBy: 'user'
            });

            if (response.ok) {
                // Mark as saved
                const saveBtn = document.getElementById('save-file-btn');
                saveBtn.classList.remove('modified');
                saveBtn.textContent = 'Save';

                // Show success feedback
                this.showSaveSuccess();

                // Refresh files list
                await this.loadProjectFiles();
            } else {
                throw new Error('Failed to save file');
            }
        } catch (error) {
            console.error('Error saving file:', error);
            alert('Failed to save file. Please try again.');
        }
    }

    showSaveSuccess() {
        const saveBtn = document.getElementById('save-file-btn');
        const originalText = saveBtn.textContent;
        saveBtn.textContent = 'Saved!';
        saveBtn.style.background = '#28a745';

        setTimeout(() => {
            saveBtn.textContent = originalText;
            saveBtn.style.background = '';
        }, 2000);
    }

    async exportCurrentFileToPDF() {
        if (!this.currentOpenFile || !this.currentFileContent) {
            alert('No file is currently open or content is empty.');
            return;
        }

        try {
            // Initialize PDF generator
            const pdfGenerator = new PDFGenerator();
            
            // Get current content from editor
            const markdownTextarea = document.getElementById('markdown-editor');
            const isMarkdownMode = markdownTextarea.style.display !== 'none';
            
            let content = this.currentFileContent;
            if (isMarkdownMode) {
                content = markdownTextarea.value;
            } else if (this.milkdownEditor) {
                content = this.milkdownEditor.getContent();
            }

            // Generate filename and title
            const filename = this.currentOpenFile.replace(/\.[^/.]+$/, '') + '.pdf';
            const title = this.currentOpenFile.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');

            // Generate and download PDF with async image processing
            await pdfGenerator.generatePDFAsync(content, filename, title);
            
            // Show success message
            this.showNotification('PDF exported successfully!', 'success');
            
        } catch (error) {
            console.error('Error exporting PDF:', error);
            this.showNotification('Failed to export PDF. Please try again.', 'error');
        }
    }

    showPDFExportModal() {
        alert('PDF export is not yet implemented. Please use the markdown content directly or copy the text for now.');
        return;
        // PDF export modal removed - not implemented
        const modal = document.createElement('div');
        modal.className = 'modal pdf-export-modal';
        modal.style.display = 'flex';

        modal.innerHTML = `
            <div class="modal-content">
                <div class="modal-header">
                    <h3>Export to PDF</h3>
                    <button class="close-btn">&times;</button>
                </div>
                <div class="modal-body">
                    <div class="export-options">
                        <div class="form-group">
                            <label>Export Type:</label>
                            <div class="radio-group">
                                <label class="radio-option">
                                    <input type="radio" name="export-type" value="single" checked>
                                    <span>Current File Only</span>
                                </label>
                                <label class="radio-option">
                                    <input type="radio" name="export-type" value="project">
                                    <span>Entire Project</span>
                                </label>
                            </div>
                        </div>

                        <div class="form-group">
                            <label for="pdf-title">Document Title:</label>
                            <input type="text" id="pdf-title" value="${this.currentOpenFile ? this.currentOpenFile.replace('.md', '') : 'Document'}">
                        </div>

                        <div class="form-group">
                            <label for="pdf-author">Author:</label>
                            <input type="text" id="pdf-author" value="Document Acceleration Tool">
                        </div>

                        <div class="form-group">
                            <label for="pdf-format">Page Format:</label>
                            <select id="pdf-format">
                                <option value="A4">A4</option>
                                <option value="Letter">Letter</option>
                                <option value="Legal">Legal</option>
                            </select>
                        </div>

                        <div class="form-group">
                            <label class="checkbox-option">
                                <input type="checkbox" id="include-metadata" checked>
                                <span>Include document metadata</span>
                            </label>
                        </div>

                        <div class="form-group project-options" style="display: none;">
                            <label class="checkbox-option">
                                <input type="checkbox" id="include-toc" checked>
                                <span>Include table of contents</span>
                            </label>
                        </div>

                        <div class="form-group project-options" style="display: none;">
                            <label class="checkbox-option">
                                <input type="checkbox" id="include-overview" checked>
                                <span>Include project overview</span>
                            </label>
                        </div>
                    </div>
                    
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary cancel-btn">Cancel</button>
                        <button type="button" class="btn btn-primary export-btn">
                            <span class="btn-text">Export PDF</span>
                            <span class="btn-loading" style="display: none;">Generating...</span>
                        </button>
                    </div>
                </div>
            </div>
        `;

        document.body.appendChild(modal);

        // Show/hide project options based on export type
        modal.querySelectorAll('input[name="export-type"]').forEach(radio => {
            radio.addEventListener('change', () => {
                const projectOptions = modal.querySelectorAll('.project-options');
                if (radio.value === 'project') {
                    projectOptions.forEach(opt => opt.style.display = 'block');
                } else {
                    projectOptions.forEach(opt => opt.style.display = 'none');
                }
            });
        });

        // Modal controls
        modal.querySelector('.close-btn').addEventListener('click', () => {
            document.body.removeChild(modal);
        });

        modal.querySelector('.cancel-btn').addEventListener('click', () => {
            document.body.removeChild(modal);
        });

        modal.querySelector('.export-btn').addEventListener('click', async () => {
            await this.performPDFExport(modal);
        });

        modal.addEventListener('click', (e) => {
            if (e.target === modal) {
                document.body.removeChild(modal);
            }
        });
    }

    async performPDFExport(modal) {
        alert('PDF export is not yet implemented. Please use the markdown content directly or copy the text for now.');
        return;
        // PDF export function removed - not implemented
        const exportBtn = modal.querySelector('.export-btn');
        const btnText = exportBtn.querySelector('.btn-text');
        const btnLoading = exportBtn.querySelector('.btn-loading');

        // Show loading state
        exportBtn.disabled = true;
        btnText.style.display = 'none';
        btnLoading.style.display = 'inline';

        try {
            const exportType = modal.querySelector('input[name="export-type"]:checked').value;
            const options = {
                title: modal.querySelector('#pdf-title').value,
                author: modal.querySelector('#pdf-author').value,
                format: modal.querySelector('#pdf-format').value,
                includeMetadata: modal.querySelector('#include-metadata').checked
            };

            let response, filename;

            if (exportType === 'single') {
                // Export single file
                response = await AuthManager.post(`/api/projects/${this.currentProject}/files/${this.currentOpenFile}/pdf`, options);
                filename = `${this.currentOpenFile.replace('.md', '')}.pdf`;
            } else {
                // Export entire project
                options.includeTableOfContents = modal.querySelector('#include-toc').checked;
                options.includeProjectOverview = modal.querySelector('#include-overview').checked;

                response = await AuthManager.post(`/api/projects/${this.currentProject}/pdf`, options);

                // Get project name for filename
                const projectResponse = await AuthManager.get(`/api/projects/${this.currentProject}`);
                const project = await projectResponse.json();
                filename = `${project.name.replace(/[^a-zA-Z0-9]/g, '-')}-complete.pdf`;
            }

            if (response.ok) {
                const blob = await response.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);

                // Close modal
                document.body.removeChild(modal);

                // Show success feedback
                this.showPDFExportSuccess(filename);
            } else {
                const error = await response.json();
                throw new Error(error.error || 'Failed to generate PDF');
            }

        } catch (error) {
            console.error('Error exporting PDF:', error);
            alert(`Failed to export PDF: ${error.message}`);
        } finally {
            // Reset button state
            exportBtn.disabled = false;
            btnText.style.display = 'inline';
            btnLoading.style.display = 'none';
        }
    }

    showPDFExportSuccess(filename) {
        // PDF export success function removed - not implemented
        return;
        const notification = document.createElement('div');
        notification.className = 'file-operation-feedback';
        notification.innerHTML = `
            <strong>PDF Export Successful!</strong>
            <p>${filename} has been downloaded</p>
        `;

        document.body.appendChild(notification);

        setTimeout(() => {
            if (notification.parentElement) {
                document.body.removeChild(notification);
            }
        }, 4000);
    }

    closeEditor() {
        // Check for unsaved changes (only for text files that have a save button)
        const saveBtn = document.getElementById('save-file-btn');
        if (saveBtn && saveBtn.classList.contains('modified')) {
            if (!confirm('You have unsaved changes. Are you sure you want to close?')) {
                return;
            }
        }

        // Clean up editor instances
        if (this.simpleEditor) {
            this.simpleEditor = null;
        }
        if (this.toastEditor) {
            this.toastEditor.destroy();
            this.toastEditor = null;
        }
        if (this.stackeditInstance) {
            this.stackeditInstance.close();
            this.stackeditInstance = null;
        }
        if (this.milkdownEditor) {
            this.milkdownEditor.destroy();
            this.milkdownEditor = null;
        }

        // Reset editor views
        const milkdownEditor = document.getElementById('editor');
        const markdownTextarea = document.getElementById('markdown-editor');
        const toggleBtn = document.getElementById('toggle-editor-mode-btn');

        milkdownEditor.style.display = 'block';
        markdownTextarea.style.display = 'none';
        markdownTextarea.value = '';
        toggleBtn.textContent = 'Markdown';
        toggleBtn.title = 'Switch to markdown mode';

        // Hide editor and show no-file-selected state
        document.querySelector('.editor-container').style.display = 'none';
        document.getElementById('no-file-selected').style.display = 'flex';

        // Clear file selection in sidebar
        this.updateFileSelection(null);

        // Reset state
        this.currentOpenFile = null;
        this.currentFileContent = null;
        saveBtn.classList.remove('modified');
        saveBtn.textContent = 'Save';
    }



    async showFileVersions(filename) {
        try {
            const response = await AuthManager.get(`/api/projects/${this.currentProject}/files/${filename}/versions`);
            if (!response.ok) {
                throw new Error('Failed to load file versions');
            }

            const versions = await response.json();

            // Sort versions in reverse order (newest first)
            const sortedVersions = [...versions].sort((a, b) => b.version - a.version);

            const modal = document.createElement('div');
            modal.className = 'modal-overlay';
            modal.innerHTML = `
                <div class="modal-content" style="max-width: 1200px; max-height: 90vh;">
                    <div class="modal-header">
                        <h3>📚 File Versions: ${filename}</h3>
                        <button class="modal-close">&times;</button>
                    </div>
                    <div class="modal-body" style="max-height: calc(90vh - 80px); overflow-y: auto;">
                        <!-- Version Comparison Section -->
                        <div class="version-compare-section" style="margin-bottom: 30px; padding-bottom: 30px; border-bottom: 2px solid #e1e8ed;">
                            <h4 style="margin-bottom: 15px; color: #2c3e50;">Compare Versions</h4>
                            <div class="version-compare-header">
                                <div class="version-selectors">
                                    <div class="version-selector">
                                        <label>Version 1:</label>
                                        <select id="version1-select">
                                            ${sortedVersions.map((v, idx) =>
                `<option value="${v.version}" ${idx === 1 ? 'selected' : ''}>v${v.version} (${new Date(v.createdAt).toLocaleDateString()})</option>`
            ).join('')}
                                        </select>
                                    </div>
                                    <div class="version-selector">
                                        <label>Version 2:</label>
                                        <select id="version2-select">
                                            ${sortedVersions.map((v, idx) =>
                `<option value="${v.version}" ${idx === 0 ? 'selected' : ''}>v${v.version} (${new Date(v.createdAt).toLocaleDateString()})</option>`
            ).join('')}
                                        </select>
                                    </div>
                                </div>
                                <div style="display: flex; gap: 8px;">
                                    <button id="compare-btn" class="btn btn-primary">Compare</button>
                                    <button id="delete-file-btn" class="btn btn-danger" style="background-color: #dc3545;">Delete File</button>
                                </div>
                            </div>
                            <div id="changes-summary" class="changes-summary" style="display: none; margin-top: 15px;"></div>
                            <div id="diff-viewer" class="diff-viewer" style="display: none; margin-top: 15px;"></div>
                        </div>

                        <!-- Versions List Section -->
                        <h4 style="margin-bottom: 15px; color: #2c3e50;">All Versions</h4>
                        <div class="versions-list">
                            ${sortedVersions.map(version => `
                                <div class="version-item">
                                    <div class="version-info">
                                        <strong>Version ${version.version}</strong>
                                        <span class="version-date">${new Date(version.createdAt).toLocaleString()}</span>
                                        <span class="version-author">by ${version.createdBy}</span>
                                    </div>
                                    <div class="version-actions">
                                        <button class="btn btn-secondary view-version-btn" data-filename="${filename}" data-version="${version.version}">View</button>
                                        <button class="btn btn-primary restore-version-btn" data-filename="${filename}" data-version="${version.version}">Restore</button>
                                    </div>
                                </div>
                            `).join('')}
                        </div>
                    </div>
                </div>
            `;

            document.body.appendChild(modal);

            // Handle modal close
            modal.querySelector('.modal-close').addEventListener('click', () => {
                document.body.removeChild(modal);
            });

            modal.addEventListener('click', (e) => {
                if (e.target === modal) {
                    document.body.removeChild(modal);
                }
            });

            // Handle compare button
            modal.querySelector('#compare-btn').addEventListener('click', async () => {
                const version1 = modal.querySelector('#version1-select').value;
                const version2 = modal.querySelector('#version2-select').value;

                if (version1 === version2) {
                    alert('Please select different versions to compare');
                    return;
                }

                await this.performVersionComparison(modal, filename, version1, version2);
            });

            // Handle delete file button
            modal.querySelector('#delete-file-btn').addEventListener('click', async () => {
                const confirmMessage = `Are you sure you want to delete "${filename}" and all its versions?\n\nThis action cannot be undone.`;

                if (confirm(confirmMessage)) {
                    try {
                        await this.deleteFile(filename);
                        document.body.removeChild(modal);
                        alert(`File "${filename}" and all its versions have been deleted.`);
                        await this.loadProjectFiles();
                    } catch (error) {
                        console.error('Error deleting file:', error);
                        alert('Failed to delete file. Please try again.');
                    }
                }
            });

            // Handle version action buttons
            modal.querySelectorAll('.view-version-btn').forEach(btn => {
                btn.addEventListener('click', () => {
                    const filename = btn.dataset.filename;
                    const version = btn.dataset.version;
                    document.body.removeChild(modal);
                    this.openFileVersion(filename, parseInt(version));
                });
            });

            modal.querySelectorAll('.restore-version-btn').forEach(btn => {
                btn.addEventListener('click', () => {
                    const filename = btn.dataset.filename;
                    const version = btn.dataset.version;
                    document.body.removeChild(modal);
                    this.restoreFileVersion(filename, parseInt(version));
                });
            });

        } catch (error) {
            console.error('Error showing file versions:', error);
            alert('Failed to load file versions');
        }
    }

    async openFileVersion(filename, version) {
        try {
            const response = await AuthManager.get(`/api/projects/${this.currentProject}/files/${filename}/versions/${version}`);
            if (!response.ok) {
                throw new Error('Failed to load version');
            }

            const versionData = await response.json();

            // Show the editor container
            document.getElementById('no-file-selected').style.display = 'none';
            document.querySelector('.editor-container').style.display = 'flex';

            // Update current file info
            this.currentOpenFile = filename;
            document.getElementById('current-file-name').textContent = `${filename} (Version ${version} - Read Only)`;

            // Initialize editor in read-only mode with version content
            const container = document.getElementById('editor');
            await this.initializeStackEdit(versionData.content, filename, true);

        } catch (error) {
            console.error('Error opening file version:', error);
            alert('Failed to open file version');
        }
    }

    async restoreFileVersion(filename, version) {
        if (!confirm(`Are you sure you want to restore ${filename} to version ${version}? This will create a new version.`)) {
            return;
        }

        try {
            const response = await AuthManager.get(`/api/projects/${this.currentProject}/files/${filename}/versions/${version}`);
            if (!response.ok) {
                throw new Error('Failed to load version');
            }

            const versionData = await response.json();

            // Save as new version
            const saveResponse = await AuthManager.put(`/api/projects/${this.currentProject}/files/${filename}`, {
                content: versionData.content,
                createdBy: 'user'
            });

            if (saveResponse.ok) {
                alert('File restored successfully!');
                await this.loadProjectFiles();

                // Close any open modals
                document.querySelectorAll('.modal').forEach(modal => {
                    document.body.removeChild(modal);
                });
            } else {
                throw new Error('Failed to restore version');
            }

        } catch (error) {
            console.error('Error restoring file version:', error);
            alert('Failed to restore file version.');
        }
    }



    async performVersionComparison(modal, filename, version1, version2) {
        try {
            const comparison = await this.fileManager.compareVersions(filename, version1, version2);
            const summary = VersionComparator.getChangesSummary(comparison.diff);

            // Show changes summary
            const summaryDiv = modal.querySelector('#changes-summary');
            summaryDiv.innerHTML = `
                <div class="change-stat added">+${summary.added} added</div>
                <div class="change-stat removed">-${summary.removed} removed</div>
                <div class="change-stat changed">~${summary.changed} changed</div>
                <div class="change-stat unchanged">${summary.unchanged} unchanged</div>
            `;
            summaryDiv.style.display = 'flex';

            // Show diff
            const diffViewer = modal.querySelector('#diff-viewer');
            diffViewer.innerHTML = VersionComparator.renderDiff(comparison.diff);
            diffViewer.style.display = 'block';

        } catch (error) {
            console.error('Error performing version comparison:', error);
            alert('Failed to compare versions.');
        }
    }

    async deleteFile(filename) {
        try {
            const response = await AuthManager.delete(`/api/projects/${this.currentProject}/files/${filename}`);

            if (!response.ok) {
                throw new Error('Failed to delete file');
            }

            console.log(`File ${filename} and all its versions deleted successfully`);

            // Close the file if it's currently open
            if (this.currentOpenFile === filename) {
                this.closeCurrentFile();
            }

        } catch (error) {
            console.error('Error deleting file:', error);
            throw error;
        }
    }

    async switchMode(mode) {
        try {
            console.log(`Switching mode to: ${mode}`);

            const response = await AuthManager.put(`/api/llm/chat/${this.currentProject}/mode`, { mode });

            if (response.ok) {
                const result = await response.json();
                console.log('Mode switched successfully:', result.context);
                this.updateModeUI(result.context);

                // Show notification
                this.showNotification(`Switched to ${mode.toUpperCase()} mode`, 'success');
            } else {
                throw new Error('Failed to switch mode');
            }
        } catch (error) {
            console.error('Error switching mode:', error);
            this.showNotification('Failed to switch mode', 'error');
        }
    }

    async sendMessage() {
        const chatInput = document.getElementById('chat-input');
        const sendBtn = document.getElementById('send-btn');
        const message = chatInput.value.trim();

        if (!message) return;

        // Clear input and disable send button
        chatInput.value = '';
        sendBtn.disabled = true;

        // Add user message to UI immediately
        this.addMessageToUI('user', message);

        // Add typing indicator
        const typingIndicator = this.addTypingIndicator();

        try {
            // Use streaming for better user experience
            await this.sendStreamingMessage(message, typingIndicator);
        } catch (error) {
            console.error('Error sending message:', error);

            // Remove typing indicator if it still exists
            if (typingIndicator.parentNode) {
                typingIndicator.remove();
            }

            // Try fallback to non-streaming API
            try {
                console.log('Attempting fallback to non-streaming API...');
                const response = await AuthManager.post('/api/llm/chat', {
                    projectId: this.currentProject,
                    message: message
                });

                if (response.ok) {
                    const result = await response.json();
                    this.addMessageToUI('assistant', result.response);
                    if (result.sessionContext) {
                        this.updateModeUI(result.sessionContext);
                    }
                } else {
                    // Try to extract error message from response
                    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                    try {
                        const errorData = await response.json();
                        if (errorData.error) {
                            errorMessage = errorData.error;
                        }
                    } catch (e) {
                        // If we can't parse JSON, use the status text
                    }
                    throw new Error(errorMessage);
                }
            } catch (fallbackError) {
                console.error('Fallback also failed:', fallbackError);

                // Show user-friendly error message with actual error details
                let errorMessage = 'Sorry, I encountered an error. Please try again.';

                // Extract the actual error message if available
                const actualError = error.message || fallbackError.message || '';

                if (actualError.includes('timeout')) {
                    errorMessage = 'The request timed out. Please try again with a shorter message.';
                } else if (actualError.includes('Token is expired') || actualError.includes('aws sso login')) {
                    errorMessage = '⚠️ AWS SSO token is expired. Please run `aws sso login` in your terminal to refresh your session.';
                } else if (actualError.includes('HTTP 503') || actualError.includes('LLM unavailable')) {
                    errorMessage = 'The AI service is temporarily unavailable. Please try again in a moment.';
                } else if (actualError.includes('HTTP 404')) {
                    errorMessage = 'Project not found. Please refresh the page and try again.';
                } else if (actualError.includes('HTTP 400')) {
                    errorMessage = 'Invalid request. Please check your message and try again.';
                } else if (actualError) {
                    // Show the actual error message for debugging
                    errorMessage = `Error: ${actualError}`;
                }

                this.addMessageToUI('assistant', errorMessage);
            }
        } finally {
            // Re-enable send button
            sendBtn.disabled = false;
        }
    }

    async sendStreamingMessage(message, typingIndicator) {
        return new Promise((resolve, reject) => {
            let assistantMessageDiv = null;
            let fullResponse = '';

            console.log('Starting streaming request for project:', this.currentProject);

            // Get selected model
            const modelSelector = document.getElementById('model-selector');
            const selectedModel = modelSelector ? modelSelector.value : 'claude-3.7-sonnet';

            // Use fetch with streaming response instead of EventSource
            AuthManager.fetch('/api/llm/stream', {
                method: 'POST',
                body: JSON.stringify({
                    projectId: this.currentProject,
                    message: message,
                    model: selectedModel,
                    excludedFromContext: Array.from(this.excludedFromContext)
                })
            })
                .then(async response => {
                    console.log('Streaming response received:', response.status, response.statusText);

                    if (!response.ok) {
                        // Try to extract error message from response
                        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                        try {
                            const errorData = await response.json();
                            if (errorData.error) {
                                errorMessage = errorData.error;
                            }
                        } catch (e) {
                            // If we can't parse JSON, use the status text
                        }
                        throw new Error(errorMessage);
                    }

                    const reader = response.body.getReader();
                    const decoder = new TextDecoder();

                    // Remove typing indicator and create assistant message
                    if (typingIndicator.parentNode) {
                        typingIndicator.remove();
                    }
                    assistantMessageDiv = this.createStreamingMessage();

                    const processStream = () => {
                        return reader.read().then(({ done, value }) => {
                            if (done) {
                                console.log('Stream completed');
                                resolve();
                                return;
                            }

                            const chunk = decoder.decode(value, { stream: true });
                            console.log('Received chunk:', chunk.substring(0, 100) + '...');
                            const lines = chunk.split('\n');

                            for (const line of lines) {
                                if (line.startsWith('data: ')) {
                                    try {
                                        const jsonData = line.slice(6);
                                        console.log('Parsing JSON:', jsonData.substring(0, 100) + '...');
                                        const data = JSON.parse(jsonData);

                                        switch (data.type) {
                                            case 'start':
                                                console.log('Stream started with session:', data.sessionId);
                                                break;

                                            case 'data':
                                                // Append content to the streaming message
                                                if (assistantMessageDiv && data.content) {
                                                    console.log('Adding content:', data.content.substring(0, 50) + '...');
                                                    fullResponse += data.content;
                                                    this.updateStreamingMessage(assistantMessageDiv, fullResponse);
                                                }
                                                break;

                                            case 'end':
                                                console.log('Stream ended:', data.result);

                                                // Finalize the streaming message (stop cursor, add mode badge)
                                                if (assistantMessageDiv) {
                                                    this.finalizeStreamingMessage(
                                                        assistantMessageDiv,
                                                        data.result?.sessionContext
                                                    );
                                                }

                                                // Update UI with session context
                                                if (data.result && data.result.sessionContext) {
                                                    console.log('Updating mode UI with context:', data.result.sessionContext);
                                                    this.updateModeUI(data.result.sessionContext);

                                                    // Store the updated context
                                                    this.currentSessionContext = data.result.sessionContext;

                                                    // Always resync files after agent response to catch any new files
                                                    console.log('Resyncing files after agent response');
                                                    this.handlePostResponseFileSync(data.result.sessionContext);
                                                }
                                                resolve();
                                                return;

                                            case 'error':
                                                console.error('Stream error:', data.error);

                                                // Remove typing indicator and show error message
                                                if (typingIndicator && typingIndicator.parentNode) {
                                                    typingIndicator.remove();
                                                }
                                                if (assistantMessageDiv && assistantMessageDiv.parentNode) {
                                                    assistantMessageDiv.remove();
                                                }

                                                reject(new Error(data.error));
                                                return;
                                        }
                                    } catch (parseError) {
                                        console.error('Error parsing SSE data:', parseError, 'Line:', line);
                                    }
                                }
                            }

                            return processStream();
                        });
                    };

                    return processStream();
                })
                .catch(error => {
                    console.error('Error in streaming request:', error);
                    if (typingIndicator.parentNode) {
                        typingIndicator.remove();
                    }
                    reject(error);
                });

            // Timeout after 60 seconds
            setTimeout(() => {
                console.log('Stream timeout reached');
                reject(new Error('Request timeout'));
            }, 60000);
        });
    }

    addTypingIndicator() {
        const chatMessages = document.getElementById('chat-messages');

        const typingDiv = document.createElement('div');
        typingDiv.className = 'message assistant typing';
        typingDiv.innerHTML = `
            <div class="message-content">
                <div class="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
            </div>
        `;

        chatMessages.appendChild(typingDiv);
        setTimeout(() => {
            chatMessages.scrollTop = chatMessages.scrollHeight;
        }, 0);

        return typingDiv;
    }

    createStreamingMessage() {
        const chatMessages = document.getElementById('chat-messages');

        const messageDiv = document.createElement('div');
        messageDiv.className = 'message assistant streaming';
        messageDiv.innerHTML = `
            <div class="message-content">
                <div class="streaming-content"></div>
            </div>
            <div class="message-meta">
                <span class="message-time">${new Date().toLocaleTimeString()}</span>
            </div>
        `;

        chatMessages.appendChild(messageDiv);
        setTimeout(() => {
            chatMessages.scrollTop = chatMessages.scrollHeight;
        }, 0);

        return messageDiv;
    }

    updateStreamingMessage(messageDiv, content) {
        const contentDiv = messageDiv.querySelector('.streaming-content');

        // Process content to hide file_update content sections
        const processedContent = this.processFileUpdateContent(content);
        contentDiv.innerHTML = this.formatMessageContent(processedContent);

        // Scroll to bottom
        const chatMessages = document.getElementById('chat-messages');
        setTimeout(() => {
            chatMessages.scrollTop = chatMessages.scrollHeight;
        }, 0);
    }

    /**
     * Finalize streaming message - remove streaming class and add mode span
     */
    finalizeStreamingMessage(messageDiv, sessionContext) {
        // Remove streaming class to stop cursor blinking
        messageDiv.classList.remove('streaming');

        // Add mode span to meta section (matching standard message format)
        const metaDiv = messageDiv.querySelector('.message-meta');
        if (metaDiv && sessionContext) {
            const modeSpan = document.createElement('span');
            modeSpan.className = 'message-mode';
            modeSpan.textContent = sessionContext.mode.toUpperCase();

            // Append after the time (standard format: time first, then mode)
            metaDiv.appendChild(modeSpan);
        }
    }

    /**
     * Process content to replace <file_update> content sections with working indicators
     */
    processFileUpdateContent(content) {
        // Replace complete file_update blocks
        let processed = content.replace(
            /<file_update>([\s\S]*?)<\/file_update>/g,
            (match, updateBlock) => {
                // Extract filename, action, and reason
                const filenameMatch = /<filename>(.*?)<\/filename>/.exec(updateBlock);
                const actionMatch = /<action>(.*?)<\/action>/.exec(updateBlock);
                const reasonMatch = /<reason>(.*?)<\/reason>/.exec(updateBlock);
                const contentMatch = /<content>([\s\S]*?)<\/content>/.exec(updateBlock);

                const filename = filenameMatch ? filenameMatch[1].trim() : 'document';
                const action = actionMatch ? actionMatch[1].trim() : '📝 Working';
                const reason = reasonMatch ? reasonMatch[1].trim() : '';
                const hasContent = contentMatch && contentMatch[1].trim().length > 0;

                // Build a nice display block without --- separators
                let display = `\n\n`;
                display += `**${action}**\n\n`;
                display += `📄 File: \`${filename}\`\n\n`;

                if (hasContent) {
                    display += `⚙️ *Working...*\n\n`;
                } else {
                    display += `⏳ *Preparing...*\n\n`;
                }

                if (reason) {
                    display += `${reason}\n\n`;
                }

                return display;
            }
        );

        // Also handle incomplete file_update blocks (while streaming)
        if (content.includes('<file_update>') && !content.includes('</file_update>')) {
            // Extract what we have so far
            const partialMatch = /<file_update>([\s\S]*)$/.exec(content);
            if (partialMatch) {
                const updateBlock = partialMatch[1];
                const filenameMatch = /<filename>(.*?)<\/filename>/.exec(updateBlock);
                const actionMatch = /<action>(.*?)<\/action>/.exec(updateBlock);

                if (filenameMatch || actionMatch) {
                    const filename = filenameMatch ? filenameMatch[1].trim() : 'document';
                    const action = actionMatch ? actionMatch[1].trim() : '📝 Working';

                    // Replace the partial block with a working indicator
                    processed = content.substring(0, content.indexOf('<file_update>'));
                    processed += `\n\n`;
                    processed += `**${action}**\n\n`;
                    processed += `📄 File: \`${filename}\`\n\n`;
                    processed += `⚙️ *Working...*\n\n`;
                }
            }
        }

        return processed;
    }

    addMessageToUI(role, content) {
        const chatMessages = document.getElementById('chat-messages');

        // Remove empty state if present
        const emptyChat = chatMessages.querySelector('.empty-chat');
        if (emptyChat) {
            emptyChat.remove();
        }

        const messageDiv = document.createElement('div');
        messageDiv.className = `message ${role}`;
        messageDiv.innerHTML = `
            <div class="message-content">
                ${this.formatMessageContent(content)}
            </div>
            <div class="message-meta">
                <span class="message-time">${new Date().toLocaleTimeString()}</span>
            </div>
        `;

        chatMessages.appendChild(messageDiv);
        setTimeout(() => {
            chatMessages.scrollTop = chatMessages.scrollHeight;
        }, 0);
    }

    async uploadFile(file) {
        try {
            // Show upload feedback
            const uploadFeedback = document.createElement('div');
            uploadFeedback.className = 'upload-feedback';
            uploadFeedback.textContent = `Uploading ${file.name}...`;
            document.getElementById('chat-messages').appendChild(uploadFeedback);

            // Step 1: Get presigned URL
            const urlResponse = await AuthManager.fetch(`/api/projects/${this.currentProject}/files/upload-url`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    filename: file.name,
                    contentType: file.type || 'application/octet-stream'
                })
            });

            if (!urlResponse.ok) {
                const errorData = await urlResponse.json();
                throw new Error(errorData.error || 'Failed to get upload URL');
            }

            const { uploadUrl, key } = await urlResponse.json();

            // Step 2: Upload directly to S3
            const uploadResponse = await fetch(uploadUrl, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': file.type || 'application/octet-stream'
                }
            });

            if (!uploadResponse.ok) {
                throw new Error('Failed to upload file to storage');
            }

            // Step 3: Register file with backend
            const registerResponse = await AuthManager.fetch(`/api/projects/${this.currentProject}/files`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    key: key,
                    filename: file.name
                })
            });

            uploadFeedback.remove();

            if (registerResponse.ok) {
                const result = await registerResponse.json();
                this.addMessageToUI('user', `📎 Uploaded file: ${file.name}`);

                // Reload files list
                await this.loadProjectFiles();

                // Open the uploaded file immediately
                console.log('Opening uploaded file:', result.filename || file.name);
                await this.openFile(result.filename || file.name);

                // Clear file input
                document.getElementById('file-input').value = '';
            } else {
                const errorData = await registerResponse.json();

                // Handle DOCX rejection with helpful message
                if (errorData.fileType === 'docx') {
                    this.showNotification(
                        'DOCX files are not yet supported. Please convert your document to PDF first and upload the PDF file instead.',
                        'error',
                        8000 // Show for 8 seconds
                    );
                } else {
                    throw new Error(errorData.error || 'Upload failed');
                }
            }
        } catch (error) {
            console.error('Error uploading file:', error);

            // Show error feedback
            const errorFeedback = document.createElement('div');
            errorFeedback.className = 'upload-error';
            errorFeedback.textContent = `Failed to upload ${file.name}. Please try again.`;
            document.getElementById('chat-messages').appendChild(errorFeedback);

            // Remove error after 5 seconds
            setTimeout(() => {
                if (errorFeedback.parentNode) {
                    errorFeedback.remove();
                }
            }, 5000);
        }
    }

    async updateProjectSettings(updates) {
        try {
            const response = await AuthManager.put(`/api/projects/${this.currentProject}`, updates);

            if (!response.ok) {
                throw new Error('Failed to update project');
            }
        } catch (error) {
            console.error('Error updating project:', error);
        }
    }
}

// Export as ES module
export { DocumentAccelerationApp };

// Import and apply file management extensions after export to avoid circular dependency
import { extendDocumentAccelerationApp } from './appFileManagement.js';
extendDocumentAccelerationApp(DocumentAccelerationApp);